import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusPill } from "@/components/shared/Common";
import CompanyReviewForm from "@/components/shared/CompanyReviewForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";

function ReviewButton({ b, reviewed, onDone }) {
  const [open, setOpen] = useState(false);
  if (reviewed) {
    return <span className="text-xs text-gold-500 font-manrope font-semibold">Review submitted</span>;
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-brand hover:bg-brand-hover text-white" size="sm" data-testid={`review-company-btn-${b.id}`}>
          <MessageSquare className="w-4 h-4 mr-1" /> Review the company
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>Confidential Company Review</DialogTitle></DialogHeader>
        <CompanyReviewForm booking={b} onDone={() => { setOpen(false); onDone(); }} />
      </DialogContent>
    </Dialog>
  );
}

export default function StaffBookings() {
  const [bookings, setBookings] = useState([]);
  const [reviewedIds, setReviewedIds] = useState(new Set());

  const load = async () => {
    try {
      const { data } = await api.get("/bookings/mine");
      setBookings(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-outfit font-bold text-white">My Bookings</h1>
      {bookings.length === 0 && <div className="card-flat p-8 text-center text-white/60 font-manrope">No bookings yet.</div>}
      <div className="grid gap-4">
        {bookings.map((b) => (
          <div key={b.id} className="card-flat p-5" data-testid={`staff-booking-${b.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-outfit font-semibold text-white">{b.company_name}</div>
                <div className="text-sm text-white/60 font-manrope">{b.date || "—"} · {b.workers}× · {b.hours}h @ €{b.hourly_rate}/hr</div>
                {b.notes && <div className="text-sm text-white/70 mt-1 font-manrope">{b.notes}</div>}
              </div>
              <StatusPill status={b.status} />
            </div>
            {b.unlocked_contact && (
              <div className="mt-3 border-t pt-3 text-sm font-manrope">
                <div className="text-xs uppercase text-brand tracking-widest font-outfit font-bold mb-1">Contact Unlocked</div>
                <div>Client: {b.unlocked_contact.full_name}</div>
                <div>Email: {b.unlocked_contact.email}</div>
                <div>Phone: {b.unlocked_contact.phone}</div>
              </div>
            )}
            {b.status === "completed" && (
              <div className="mt-3 border-t pt-3 flex items-center justify-between gap-3">
                <div className="text-xs text-white/60 font-manrope">
                  Standards apply equally — help SecuSource protect professionals by sharing how this company treated you.
                </div>
                <ReviewButton b={b} reviewed={reviewedIds.has(b.id)} onDone={() => setReviewedIds((prev) => new Set([...prev, b.id]))} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
